import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Save, Plus, Upload, FileJson, Loader2, ChevronLeft, Archive,
  Sparkles, Wind, Moon, Heart, Waves, Coffee, ImageIcon, Music, Video, FileText,
  Search, Calendar, PlayCircle, RotateCcw, Undo2, LayoutDashboard,
  ListChecks, ScrollText, Clock, RefreshCw, X, CalendarDays, Image as MediaIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import CalendarTab from './admin/CalendarTab';
import MediaLibraryTab from './admin/MediaLibraryTab';
import ProgramsTab from './admin/ProgramsTab';
import ProgramMeditationsTab from './admin/ProgramMeditationsTab';
import FlagshipProgramsTab from './admin/FlagshipProgramsTab';
import ProgramAnalyticsTab from './admin/ProgramAnalyticsTab';
import LaunchCampaignTab from './admin/LaunchCampaignTab';
import EmailHealthCard from './admin/EmailHealthCard';
import { uploadMedia, abortChunkedSession, SMALL_UPLOAD_LIMIT } from '../lib/chunkedUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = [
  { id: 'meditation',       label: 'Meditation',       icon: Sparkles },
  { id: 'affirmation',      label: 'Affirmation',      icon: Sparkles },
  { id: 'reflection',       label: 'Reflection',       icon: Coffee },
  { id: 'manifestation',    label: 'Manifestation',    icon: Sparkles },
  { id: 'soundscape',       label: 'Soundscape',       icon: Waves },
  { id: 'sleep_meditation', label: 'Sleep meditation', icon: Moon },
  { id: 'sleep_story',      label: 'Sleep story',      icon: Moon },
  { id: 'workplace_reset',  label: 'Workplace reset',  icon: Coffee },
  { id: 'breathing_audio',  label: 'Breathing audio',  icon: Wind },
  { id: 'rowdy_message',    label: 'A Moment w/ Rowdy',icon: Heart },
  { id: 'video',            label: 'Video',            icon: Video },
];

const STATUS_META = {
  draft:     { label: 'Draft',     tone: 'text-amber-700  bg-amber-50  border-amber-200' },
  scheduled: { label: 'Scheduled', tone: 'text-sky-700    bg-sky-50    border-sky-200' },
  published: { label: 'Published', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  archived:  { label: 'Archived',  tone: 'text-stone-500  bg-stone-100 border-stone-200' },
};

const empty = () => ({
  type: 'meditation',
  title: '',
  slug: '',
  shortDescription: '',
  description: '',
  category: '',
  tags: '',      // comma-separated in the form; split on submit
  moods: '',
  onboardingReasons: '',
  dayparts: '',
  recommendationPriority: '',
  dailyFeatureEligible: false,
  quickResetEligible: false,
  sleepEligible: false,
  durationSeconds: '',
  coverImageUrl: '',
  posterImageUrl: '',
  audioUrl: '',
  audioAssetId: '',
  videoUrl: '',
  videoAssetId: '',
  captionsUrl: '',
  captionsAssetId: '',
  accessLevel: 'free',
  featured: false,
  status: 'draft',
  publishAt: '',
});

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('rbr_token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
};

const inputCls = 'w-full px-3 py-2.5 rounded-xl bg-white border border-stone-200 focus:border-[#C79A4A] focus:outline-none text-sm text-stone-800';

// -------------------- Small hoisted primitives (never inline in render) --------------------

const Row = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 block mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
  </div>
);

const _formatBytes = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const UploadProgress = ({ progress, onCancel, testid }) => {
  if (!progress) return null;
  const pct = Math.round(progress.pct || 0);
  const phase = progress.phase || 'uploading';
  const label = phase === 'assembling'
    ? 'Assembling on server…'
    : phase === 'complete'
    ? 'Finalising…'
    : phase === 'starting'
    ? 'Starting…'
    : `Uploading ${_formatBytes(progress.loaded || 0)} of ${_formatBytes(progress.total || progress.size || 0)}`;
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex items-center justify-between text-[11px] text-stone-600 mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#C79A4A] transition-all"
          style={{ width: `${pct}%` }}
          data-testid={`${testid}-bar`}
        />
      </div>
      {onCancel && phase !== 'complete' && phase !== 'assembling' && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-1.5 text-[11px] text-stone-500 hover:text-stone-800 underline"
          data-testid={`${testid}-cancel`}
        >
          Cancel upload
        </button>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${m.tone}`}>
      {m.label}
    </span>
  );
};

const TabButton = ({ tabKey, current, onClick, icon: Icon, children }) => (
  <button
    data-testid={`admin-tab-${tabKey}`}
    onClick={() => onClick(tabKey)}
    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-all ${
      current === tabKey ? 'bg-[#173F35] text-[#FFFCF6]' : 'bg-white text-stone-600 hover:bg-[#EFE7D4] border border-stone-200'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    {children}
  </button>
);

// -------------------- Dashboard tab --------------------

const DashboardTab = ({ dashboard, onRefresh, onRunScheduler, running, onOpenScheduled }) => {
  if (!dashboard) {
    return <div className="text-center py-16 text-stone-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard…</div>;
  }
  const s = dashboard.counts?.status || {};
  const totals = [
    { key: 'draft', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { key: 'scheduled', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    { key: 'published', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { key: 'archived', color: 'bg-stone-100 text-stone-500 border-stone-200' },
  ];
  return (
    <div data-testid="admin-dashboard-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={onRefresh} data-testid="dashboard-refresh" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 hover:border-[#C79A4A] text-[12px] text-stone-700">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          onClick={onRunScheduler}
          disabled={running}
          data-testid="dashboard-run-scheduler"
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#C79A4A] text-[#0D2B24] text-[12px] font-semibold hover:bg-[#B8873A] disabled:opacity-60"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          Run Scheduler Now
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {totals.map(t => (
          <div key={t.key} data-testid={`dashboard-count-${t.key}`} className={`rounded-2xl p-4 border ${t.color}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{t.key}</p>
            <p className="text-3xl mt-1" style={{ fontFamily: "'DM Serif Display', serif" }}>{s[t.key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[#C79A4A]" />
            <h4 className="text-sm font-semibold text-stone-800">Upcoming scheduled</h4>
          </div>
          {(dashboard.upcomingScheduled || []).length === 0 ? (
            <p className="text-[12px] text-stone-500 py-4 text-center">Nothing scheduled. Use the library to schedule content.</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {dashboard.upcomingScheduled.map(row => (
                <li key={row.id} className="py-2 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#EFE7D4] flex-shrink-0 overflow-hidden">
                    {row.coverImageUrl && <img src={row.coverImageUrl} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-stone-800 truncate">{row.title}</p>
                    <p className="text-[10px] text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(row.publishAt).toLocaleString()}</p>
                  </div>
                  <button onClick={() => onOpenScheduled(row.id)} data-testid={`dashboard-open-scheduled-${row.id}`} className="text-[10px] font-semibold text-[#C79A4A] hover:underline">Open</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <ScrollText className="w-4 h-4 text-[#C79A4A]" />
            <h4 className="text-sm font-semibold text-stone-800">Recent activity</h4>
          </div>
          {(dashboard.recentAudit || []).length === 0 ? (
            <p className="text-[12px] text-stone-500 py-4 text-center">No admin activity yet.</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {dashboard.recentAudit.slice(0, 8).map(row => (
                <li key={row.id} className="py-2">
                  <p className="text-[11px] text-stone-800">
                    <span className="font-mono font-semibold text-[#173F35]">{row.action}</span>
                    <span className="ml-1.5 text-stone-500">{row.contentType ? `· ${row.contentType}` : ''}</span>
                  </p>
                  <p className="text-[10px] text-stone-400 mt-0.5">
                    {(row.actorEmail || row.adminEmail || 'system')} · {row.source || 'admin'} · {new Date(row.createdAt).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <EmailHealthCard />
        <PushAnalyticsWidget />
      </div>
    </div>
  );
};

// -------------------- Push Analytics Widget --------------------

const PushAnalyticsWidget = () => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/admin/push/analytics?limit=15');
      const d = await r.json();
      if (r.ok) setRows(d.items || []);
      else setRows([]);
    } catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4" data-testid="push-analytics-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#C79A4A]" />
        <h4 className="text-sm font-semibold text-stone-800">Push analytics</h4>
        <button
          onClick={load}
          data-testid="push-analytics-refresh"
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-stone-200 hover:border-[#C79A4A] text-[11px] text-stone-700"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {rows === null || loading ? (
        <p className="text-[12px] text-stone-500 py-4 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading recent broadcasts…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-[12px] text-stone-500 py-4 text-center">No push broadcasts yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-[11px] border-separate border-spacing-y-1">
            <thead>
              <tr className="text-stone-500">
                <th className="text-left font-semibold pb-1">Campaign</th>
                <th className="text-left font-semibold pb-1">Sent at</th>
                <th className="text-right font-semibold pb-1">Eligible</th>
                <th className="text-right font-semibold pb-1">Sent</th>
                <th className="text-right font-semibold pb-1">Failed</th>
                <th className="text-right font-semibold pb-1">Clicks</th>
                <th className="text-right font-semibold pb-1">CTR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.campaign}-${r.sentAt}`} className="bg-white" data-testid={`push-analytics-row-${r.campaign}`}>
                  <td className="px-2 py-1.5 rounded-l-lg">
                    <p className="font-mono text-[10.5px] text-[#173F35] truncate max-w-[220px]" title={r.campaign}>{r.campaign}</p>
                    {r.title && <p className="text-[10px] text-stone-500 truncate max-w-[220px]" title={r.title}>{r.title}</p>}
                  </td>
                  <td className="px-2 py-1.5 text-stone-600 whitespace-nowrap">{r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}</td>
                  <td className="px-2 py-1.5 text-right text-stone-700">{r.eligible ?? 0}</td>
                  <td className="px-2 py-1.5 text-right text-stone-700 font-semibold">{r.sent ?? 0}</td>
                  <td className="px-2 py-1.5 text-right text-stone-500">{r.failed ?? 0}</td>
                  <td className="px-2 py-1.5 text-right text-[#C79A4A] font-semibold">{r.clicks ?? 0}</td>
                  <td className="px-2 py-1.5 text-right text-stone-700 rounded-r-lg">
                    {typeof r.ctrPercent === 'number' ? `${r.ctrPercent.toFixed(2)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const LibraryFilters = ({ filters, onChange, onSearch, onCreate }) => (
  <div className="flex flex-wrap gap-2 mb-3 items-center">
    <div className="relative flex-1 min-w-[180px]">
      <Search className="w-4 h-4 absolute top-2.5 left-3 text-stone-400" />
      <input
        type="text"
        value={filters.q}
        onChange={(e) => onChange('q', e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
        placeholder="Search title, slug, description…"
        data-testid="library-search-input"
        className={`pl-9 ${inputCls}`}
      />
    </div>
    <select data-testid="library-type-filter" value={filters.type} onChange={(e) => onChange('type', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm">
      <option value="">All types</option>
      {CONTENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
    </select>
    <select data-testid="library-status-filter" value={filters.status} onChange={(e) => onChange('status', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm">
      <option value="">All statuses</option>
      <option value="draft">Draft</option>
      <option value="scheduled">Scheduled</option>
      <option value="published">Published</option>
      <option value="archived">Archived</option>
    </select>
    <select data-testid="library-access-filter" value={filters.accessLevel} onChange={(e) => onChange('accessLevel', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm">
      <option value="">Any access</option>
      <option value="free">Free</option>
      <option value="premium">Premium</option>
    </select>
    <select data-testid="library-sort" value={filters.sort} onChange={(e) => onChange('sort', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm">
      <option value="createdAt_desc">Newest</option>
      <option value="createdAt_asc">Oldest</option>
      <option value="title_asc">Title A→Z</option>
      <option value="title_desc">Title Z→A</option>
      <option value="publishAt_asc">Publish soonest</option>
      <option value="updatedAt_desc">Recently updated</option>
    </select>
    <button onClick={onCreate} data-testid="library-new-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#C79A4A] text-[#0D2B24] text-[12px] font-semibold hover:bg-[#B8873A]">
      <Plus className="w-4 h-4" /> New
    </button>
  </div>
);

const LibraryRow = ({ row, onEdit, onArchive, onPublish, onSchedule, onUnpublish, onRestore }) => (
  <div data-testid={`library-row-${row.id}`} className="flex items-center gap-3 py-3">
    <div className="w-12 h-12 rounded-lg bg-[#EFE7D4] flex-shrink-0 overflow-hidden">
      {row.coverImageUrl && <img src={row.coverImageUrl} alt="" className="w-full h-full object-cover" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-stone-800 truncate">{row.title}</p>
        <StatusBadge status={row.status} />
        {row.accessLevel === 'premium' && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#F5EAC9] text-[#8A6B25] uppercase tracking-wider">Premium</span>
        )}
      </div>
      <p className="text-[11px] text-stone-500 mt-0.5">
        <span className="uppercase tracking-wider text-[10px] text-[#C79A4A] font-bold">{row.type}</span>
        <span className="mx-1.5 text-stone-300">·</span>
        {row.category || '—'}
        {row.status === 'scheduled' && row.publishAt && (
          <>
            <span className="mx-1.5 text-stone-300">·</span>
            <span className="text-sky-700 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(row.publishAt).toLocaleString()}</span>
          </>
        )}
      </p>
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {row.status === 'draft' && (
        <button onClick={() => onPublish(row)} data-testid={`row-publish-${row.id}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-[#C79A4A] text-[#0D2B24] hover:bg-[#B8873A]">
          Publish
        </button>
      )}
      {row.status === 'draft' && (
        <button onClick={() => onSchedule(row)} data-testid={`row-schedule-${row.id}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-sky-200 text-sky-700 hover:bg-sky-50">
          Schedule
        </button>
      )}
      {(row.status === 'published' || row.status === 'scheduled') && (
        <button onClick={() => onUnpublish(row)} data-testid={`row-unpublish-${row.id}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50">
          <Undo2 className="w-3 h-3 inline mr-1" />Unpublish
        </button>
      )}
      <button onClick={() => onEdit(row)} data-testid={`row-edit-${row.id}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-white">Edit</button>
      {row.status === 'archived' ? (
        <button onClick={() => onRestore(row)} data-testid={`row-restore-${row.id}`} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" aria-label="Restore">
          <RotateCcw className="w-4 h-4" />
        </button>
      ) : (
        <button onClick={() => onArchive(row)} data-testid={`row-archive-${row.id}`} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" aria-label="Archive">
          <Archive className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
);

const Pager = ({ page, totalPages, onChange }) => (
  <div className="flex items-center justify-between mt-3 text-[12px] text-stone-500">
    <span data-testid="library-page-info">Page {page} of {totalPages}</span>
    <div className="flex gap-2">
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1} data-testid="library-page-prev" className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-white">Prev</button>
      <button onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="library-page-next" className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-white">Next</button>
    </div>
  </div>
);

// -------------------- Schedule modal --------------------

const ScheduleModal = ({ open, onClose, onConfirm, row, saving }) => {
  const [when, setWhen] = useState('');
  useEffect(() => {
    if (open) {
      // Default: 1 hour from now, formatted for <input type="datetime-local">
      const d = new Date(Date.now() + 60 * 60 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      setWhen(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    }
  }, [open]);
  if (!open) return null;
  const submit = () => {
    if (!when) return toast.error('Pick a date & time');
    // The <input datetime-local> value is in the user's local timezone; convert to UTC ISO.
    const iso = new Date(when).toISOString();
    if (new Date(iso).getTime() <= Date.now()) return toast.error('Schedule must be in the future');
    onConfirm(iso);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="schedule-modal">
      <div className="bg-[#FFFCF6] w-full max-w-md rounded-2xl border border-stone-200 shadow-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Schedule to publish</h3>
            <p className="text-[12px] text-stone-500 mt-0.5">{row?.title}</p>
          </div>
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-700" data-testid="schedule-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <Row label="Publish at (your local time)" hint="Stored as UTC. Content stays private until this moment.">
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={inputCls} data-testid="schedule-modal-when" />
        </Row>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-stone-600 hover:bg-white">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="schedule-modal-confirm" className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// -------------------- Editor (kept as hoisted component — no inline defs) --------------------

const ContentForm = ({ initial, onSaved, onCancel, onLifecycle }) => {
  const [form, setForm] = useState(() => ({
    ...empty(),
    ...(initial || {}),
    tags: (initial?.tags || []).join(', '),
    moods: (initial?.moods || []).join(', '),
    onboardingReasons: (initial?.onboardingReasons || []).join(', '),
    dayparts: (initial?.dayparts || []).join(', '),
    recommendationPriority: initial?.recommendationPriority ?? '',
    dailyFeatureEligible: !!initial?.dailyFeatureEligible,
    quickResetEligible: !!initial?.quickResetEligible,
    sleepEligible: !!initial?.sleepEligible,
    publishAt: initial?.publishAt || '',
  }));
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingCaptions, setUploadingCaptions] = useState(false);
  const [audioProgress, setAudioProgress] = useState(null);   // { pct, phase }
  const [videoProgress, setVideoProgress] = useState(null);
  const [pendingAudioFile, setPendingAudioFile] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const editing = !!initial?.id;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildBody = () => ({
    ...form,
    tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    moods: form.moods ? form.moods.split(',').map((t) => t.trim()).filter(Boolean) : [],
    onboardingReasons: form.onboardingReasons ? form.onboardingReasons.split(',').map((t) => t.trim()).filter(Boolean) : [],
    dayparts: form.dayparts ? form.dayparts.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean) : [],
    recommendationPriority: form.recommendationPriority === '' || form.recommendationPriority == null ? null : Number(form.recommendationPriority),
    durationSeconds: form.durationSeconds ? Number(form.durationSeconds) : null,
  });

  const doSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    const path = editing ? `/api/admin/content/${initial.id}` : '/api/admin/content';
    const method = editing ? 'PATCH' : 'POST';
    try {
      const r = await authFetch(path, { method, body: JSON.stringify(buildBody()) });
      const data = await r.json();
      if (!r.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Failed to save');
        return;
      }
      toast.success(editing ? 'Saved' : 'Created');
      onSaved(data);
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, endpoint, setBusy, onDone) => {
    if (!file) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Upload failed');
        return;
      }
      onDone(data);
      toast.success('Uploaded');
    } catch {
      toast.error('Upload network error');
    } finally {
      setBusy(false);
    }
  };

  const onImageUpload = (e) => {
    const file = e.target.files?.[0];
    uploadFile(file, '/api/admin/upload/image?kind=content', setUploadingImg, (data) => {
      setField('coverImageUrl', `${API_URL}${data.url}`);
    });
  };

  const [regeneratingCover, setRegeneratingCover] = useState(false);
  // Preview-before-commit modal state. `candidate` is null until the admin
  // clicks Regenerate; it becomes { candidateAssetId, candidateCoverUrl,
  // currentCoverImageUrl } after the /preview call.
  const [coverCandidate, setCoverCandidate] = useState(null);
  const [committingCover, setCommittingCover] = useState(false);

  const authFetchToken = () => localStorage.getItem('rbr_token');

  const requestCoverCandidate = async () => {
    setRegeneratingCover(true);
    try {
      const r = await authFetch(`/api/admin/content/${initial.id}/regenerate-cover/preview`, {
        method: 'POST', body: JSON.stringify({}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Cover generation failed');
        return null;
      }
      return data;
    } catch {
      toast.error('Network error while generating cover');
      return null;
    } finally {
      setRegeneratingCover(false);
    }
  };

  const onRegenerateCover = async () => {
    if (!editing || !initial?.id) {
      toast.error('Save this content first before regenerating a cover');
      return;
    }
    if (!form.title?.trim()) {
      toast.error('A title is required to regenerate a branded cover');
      return;
    }
    const first = await requestCoverCandidate();
    if (first) setCoverCandidate(first);
  };

  const onCoverRegenerateAgain = async () => {
    // Discard the previous candidate first (best-effort), then generate a new one.
    if (coverCandidate?.candidateAssetId) {
      authFetch(`/api/admin/content/${initial.id}/regenerate-cover/discard`, {
        method: 'POST',
        body: JSON.stringify({ candidateAssetId: coverCandidate.candidateAssetId }),
      }).catch(() => { /* best effort */ });
    }
    const next = await requestCoverCandidate();
    if (next) setCoverCandidate(next);
  };

  const onCoverCommit = async () => {
    if (!coverCandidate?.candidateAssetId) return;
    setCommittingCover(true);
    try {
      const r = await authFetch(`/api/admin/content/${initial.id}/regenerate-cover/commit`, {
        method: 'POST',
        body: JSON.stringify({ candidateAssetId: coverCandidate.candidateAssetId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Cover commit failed');
        return;
      }
      const nextUrl = data.coverImageUrl?.startsWith('http')
        ? data.coverImageUrl
        : `${API_URL}${data.coverImageUrl}`;
      setField('coverImageUrl', `${nextUrl}?v=${Date.now()}`);
      toast.success('New emblem cover applied');
      setCoverCandidate(null);
    } catch {
      toast.error('Network error while committing new cover');
    } finally {
      setCommittingCover(false);
    }
  };

  const onCoverCancel = () => {
    // Discard the candidate so the temporary asset doesn't linger.
    if (coverCandidate?.candidateAssetId) {
      authFetch(`/api/admin/content/${initial.id}/regenerate-cover/discard`, {
        method: 'POST',
        body: JSON.stringify({ candidateAssetId: coverCandidate.candidateAssetId }),
      }).catch(() => { /* best effort */ });
    }
    setCoverCandidate(null);
  };

  const doChunkedOrDirect = async (file, kind, setBusy, setProgress, setPending, onDone) => {
    if (!file) return;
    setPending(file);
    setBusy(true);
    setProgress({ pct: 0, phase: 'starting', size: file.size, name: file.name });
    const token = localStorage.getItem('rbr_token');
    try {
      const asset = await uploadMedia({
        file,
        kind,
        apiUrl: API_URL,
        token,
        onProgress: ({ loaded, total, phase }) => {
          const pct = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;
          setProgress({ pct, phase, loaded, total, size: file.size, name: file.name });
        },
      });
      onDone(asset);
      if (file.size > SMALL_UPLOAD_LIMIT) {
        toast.success(asset.deduped ? 'Uploaded (deduped)' : 'Large file uploaded');
      } else {
        toast.success('Uploaded');
      }
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      setProgress(null);
      setPending(null);
    }
  };

  const cancelAudio = async () => {
    if (!pendingAudioFile) return;
    const token = localStorage.getItem('rbr_token');
    await abortChunkedSession({ file: pendingAudioFile, kind: 'audio', apiUrl: API_URL, token });
    toast.info('Audio upload cancelled');
  };
  const cancelVideo = async () => {
    if (!pendingVideoFile) return;
    const token = localStorage.getItem('rbr_token');
    await abortChunkedSession({ file: pendingVideoFile, kind: 'video', apiUrl: API_URL, token });
    toast.info('Video upload cancelled');
  };

  const onAudioUpload = (e) => {
    const file = e.target.files?.[0];
    doChunkedOrDirect(file, 'audio', setUploadingAudio, setAudioProgress, setPendingAudioFile, (data) => {
      setField('audioAssetId', data.id);
      setField('audioUrl', `${API_URL}${data.url}`);
    });
  };
  const onVideoUpload = (e) => {
    const file = e.target.files?.[0];
    doChunkedOrDirect(file, 'video', setUploadingVideo, setVideoProgress, setPendingVideoFile, (data) => {
      setField('videoAssetId', data.id);
      setField('videoUrl', `${API_URL}${data.url}`);
      setField('type', 'video');
    });
  };
  const onCaptionsUpload = (e) => {
    const file = e.target.files?.[0];
    uploadFile(file, '/api/admin/upload/captions', setUploadingCaptions, (data) => {
      setField('captionsAssetId', data.id);
      setField('captionsUrl', `${API_URL}${data.url}`);
    });
  };

  return (
    <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid="content-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
          {editing ? 'Edit content' : 'New content'}
        </h3>
        {editing && initial?.status && <StatusBadge status={initial.status} />}
      </div>

      <Row label="Type">
        <select className={inputCls} value={form.type} onChange={(e) => setField('type', e.target.value)} data-testid="admin-content-type">
          {CONTENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </Row>

      <Row label="Title">
        <input className={inputCls} value={form.title} onChange={(e) => setField('title', e.target.value)} maxLength={200} data-testid="admin-content-title" />
      </Row>

      <Row label="Slug" hint="Optional. Auto-generated from title if left blank.">
        <input className={inputCls} value={form.slug} onChange={(e) => setField('slug', e.target.value)} maxLength={200} placeholder="for-now-just-breathe" />
      </Row>

      <div className="grid grid-cols-2 gap-3">
        <Row label="Category" hint="e.g. peace, self-worth, sleep">
          <input className={inputCls} value={form.category} onChange={(e) => setField('category', e.target.value)} maxLength={80} />
        </Row>
        <Row label="Duration (sec)">
          <input type="number" min="0" max={6 * 3600} className={inputCls} value={form.durationSeconds} onChange={(e) => setField('durationSeconds', e.target.value)} />
        </Row>
      </div>

      <Row label="Short description">
        <input className={inputCls} value={form.shortDescription} onChange={(e) => setField('shortDescription', e.target.value)} maxLength={400} data-testid="admin-content-short" />
      </Row>

      <Row label="Full description">
        <textarea rows={5} className={inputCls} value={form.description} onChange={(e) => setField('description', e.target.value)} maxLength={8000} />
      </Row>

      <div className="grid grid-cols-2 gap-3">
        <Row label="Tags" hint="Comma separated. e.g. quick-reset, breath">
          <input className={inputCls} value={form.tags} onChange={(e) => setField('tags', e.target.value)} />
        </Row>
        <Row label="Moods" hint="e.g. anxious, overwhelmed, peaceful">
          <input className={inputCls} value={form.moods} onChange={(e) => setField('moods', e.target.value)} />
        </Row>
      </div>

      <Row label="Cover image">
        <div className="flex gap-2 items-center flex-wrap">
          <input className={inputCls} value={form.coverImageUrl} onChange={(e) => setField('coverImageUrl', e.target.value)} placeholder="https://…" />
          <label className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-[12px] font-semibold cursor-pointer hover:border-[#C79A4A] ${uploadingImg ? 'opacity-60' : ''}`}>
            {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            Upload
            <input type="file" accept="image/*" className="hidden" onChange={onImageUpload} data-testid="admin-content-image-upload" />
          </label>
          <button
            type="button"
            onClick={onRegenerateCover}
            disabled={regeneratingCover || !editing}
            data-testid="admin-content-regenerate-cover"
            title={editing ? 'Generate a new branded emblem cover with Nano Banana' : 'Save this item first to enable cover regeneration'}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-[12px] font-semibold transition ${
              !editing
                ? 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'
                : 'border-[#173F35] bg-[#173F35] text-[#FFFCF6] hover:bg-[#0D2B24]'
            } ${regeneratingCover ? 'opacity-60' : ''}`}
          >
            {regeneratingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {regeneratingCover ? 'Regenerating…' : 'Regenerate Emblem Cover'}
          </button>
        </div>
        {form.coverImageUrl && <img src={form.coverImageUrl} alt="" data-testid="admin-content-cover-preview" className="mt-2 w-24 h-24 rounded-xl object-cover border border-stone-200" />}
      </Row>

      <Row label="Audio">
        <div className="flex gap-2 items-center">
          <input className={inputCls} value={form.audioUrl} onChange={(e) => setField('audioUrl', e.target.value)} placeholder="https://…" />
          <label className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-[12px] font-semibold cursor-pointer hover:border-[#C79A4A] ${uploadingAudio ? 'opacity-60' : ''}`}>
            {uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />}
            Upload
            <input type="file" accept="audio/*" className="hidden" onChange={onAudioUpload} data-testid="admin-content-audio-upload" />
          </label>
        </div>
        {audioProgress && (
          <UploadProgress
            progress={audioProgress}
            onCancel={cancelAudio}
            testid="admin-content-audio-progress"
          />
        )}
        {form.audioAssetId && <p className="text-[11px] text-stone-500 mt-1">Asset ID: {form.audioAssetId}</p>}
      </Row>

      <Row label="Video (MP4)" hint="Uploads to Emergent Object Storage. Streams via /api/media/{id} with HTTP Range. Files > 90MB auto-use chunked upload.">
        <div className="flex gap-2 items-center">
          <input className={inputCls} value={form.videoUrl} onChange={(e) => setField('videoUrl', e.target.value)} placeholder="https://…" data-testid="admin-content-video-url" />
          <label className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-[12px] font-semibold cursor-pointer hover:border-[#C79A4A] ${uploadingVideo ? 'opacity-60' : ''}`}>
            {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
            Upload
            <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={onVideoUpload} data-testid="admin-content-video-upload" />
          </label>
        </div>
        {videoProgress && (
          <UploadProgress
            progress={videoProgress}
            onCancel={cancelVideo}
            testid="admin-content-video-progress"
          />
        )}
        {form.videoAssetId && <p className="text-[11px] text-stone-500 mt-1">Asset ID: {form.videoAssetId}</p>}
      </Row>

      <Row label="Poster image" hint="Optional. Shown before the video plays. Falls back to Cover image.">
        <input className={inputCls} value={form.posterImageUrl} onChange={(e) => setField('posterImageUrl', e.target.value)} placeholder="https://…" />
      </Row>

      <Row label="Captions (VTT)" hint="Optional WebVTT file for accessibility.">
        <div className="flex gap-2 items-center">
          <input className={inputCls} value={form.captionsUrl} onChange={(e) => setField('captionsUrl', e.target.value)} placeholder="https://…" />
          <label className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-[12px] font-semibold cursor-pointer hover:border-[#C79A4A] ${uploadingCaptions ? 'opacity-60' : ''}`}>
            {uploadingCaptions ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Upload
            <input type="file" accept=".vtt,.srt,text/vtt" className="hidden" onChange={onCaptionsUpload} data-testid="admin-content-captions-upload" />
          </label>
        </div>
        {form.captionsAssetId && <p className="text-[11px] text-stone-500 mt-1">Asset ID: {form.captionsAssetId}</p>}
      </Row>

      {/* --- Personalization --- */}
      <div className="mt-6 mb-3 pt-4 border-t border-stone-200">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#C79A4A]">Personalization</p>
        <p className="text-[11px] text-stone-400 mt-0.5">Tune where this content surfaces on Today.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Row label="Onboarding reasons" hint="Comma-separated. e.g. stress, sleep, healing, motivation, peace">
          <input className={inputCls} value={form.onboardingReasons} onChange={(e) => setField('onboardingReasons', e.target.value)} data-testid="admin-content-onboarding-reasons" placeholder="stress, sleep" />
        </Row>
        <Row label="Dayparts" hint="Comma-separated: morning, afternoon, evening, night">
          <input className={inputCls} value={form.dayparts} onChange={(e) => setField('dayparts', e.target.value)} data-testid="admin-content-dayparts" placeholder="morning, evening" />
        </Row>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Row label="Priority" hint="-100 to +100">
          <input type="number" min="-100" max="100" className={inputCls} value={form.recommendationPriority} onChange={(e) => setField('recommendationPriority', e.target.value)} data-testid="admin-content-priority" />
        </Row>
        <Row label="Daily feature">
          <label className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-stone-200 bg-white cursor-pointer">
            <input type="checkbox" checked={form.dailyFeatureEligible} onChange={(e) => setField('dailyFeatureEligible', e.target.checked)} data-testid="admin-content-daily-eligible" />
            <span className="text-[12px] text-stone-700">Eligible</span>
          </label>
        </Row>
        <Row label="Quick reset">
          <label className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-stone-200 bg-white cursor-pointer">
            <input type="checkbox" checked={form.quickResetEligible} onChange={(e) => setField('quickResetEligible', e.target.checked)} data-testid="admin-content-quick-reset-eligible" />
            <span className="text-[12px] text-stone-700">Eligible</span>
          </label>
        </Row>
        <Row label="Sleep">
          <label className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-stone-200 bg-white cursor-pointer">
            <input type="checkbox" checked={form.sleepEligible} onChange={(e) => setField('sleepEligible', e.target.checked)} data-testid="admin-content-sleep-eligible" />
            <span className="text-[12px] text-stone-700">Eligible</span>
          </label>
        </Row>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Row label="Access">
          <select className={inputCls} value={form.accessLevel} onChange={(e) => setField('accessLevel', e.target.value)}>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
        </Row>
        <Row label="Status">
          <select className={inputCls} value={form.status} onChange={(e) => setField('status', e.target.value)} data-testid="admin-content-status">
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </Row>
        <Row label="Featured">
          <label className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-stone-200 bg-white cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setField('featured', e.target.checked)} />
            <span className="text-sm text-stone-700">Featured</span>
          </label>
        </Row>
      </div>

      {editing && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-stone-200">
          <p className="w-full text-[11px] font-semibold uppercase tracking-wider text-stone-500">Lifecycle</p>
          <button onClick={() => onLifecycle('publish', initial)} data-testid="editor-publish-now" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#C79A4A] text-[#0D2B24] text-[12px] font-semibold hover:bg-[#B8873A]">
            <PlayCircle className="w-4 h-4" /> Publish now
          </button>
          <button onClick={() => onLifecycle('schedule', initial)} data-testid="editor-schedule" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-200 text-sky-700 text-[12px] font-semibold hover:bg-sky-50">
            <Calendar className="w-4 h-4" /> Schedule…
          </button>
          {(initial?.status === 'published' || initial?.status === 'scheduled') && (
            <button onClick={() => onLifecycle('unpublish', initial)} data-testid="editor-unpublish" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-stone-700 text-[12px] font-semibold hover:bg-stone-50">
              <Undo2 className="w-4 h-4" /> Unpublish
            </button>
          )}
          {initial?.status === 'archived' ? (
            <button onClick={() => onLifecycle('restore', initial)} data-testid="editor-restore" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-50">
              <RotateCcw className="w-4 h-4" /> Restore
            </button>
          ) : (
            <button onClick={() => onLifecycle('archive', initial)} data-testid="editor-archive" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-200 text-rose-600 text-[12px] font-semibold hover:bg-rose-50">
              <Archive className="w-4 h-4" /> Archive
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-5 pt-4 border-t border-stone-200">
        <button onClick={doSave} disabled={saving} data-testid="admin-content-save" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60 active:scale-95 transition-all">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {editing ? 'Save changes' : 'Create draft'}
        </button>
        <button onClick={onCancel} className="px-4 py-2.5 rounded-xl text-sm text-stone-600 hover:bg-white" data-testid="admin-content-cancel">Cancel</button>
      </div>
      {coverCandidate && (
        <CoverPreviewModal
          candidate={coverCandidate}
          title={form.title}
          isBusy={regeneratingCover || committingCover}
          isRegenerating={regeneratingCover}
          isCommitting={committingCover}
          onRegenerateAgain={onCoverRegenerateAgain}
          onCommit={onCoverCommit}
          onCancel={onCoverCancel}
        />
      )}
    </div>
  );
};

const CoverPreviewModal = ({
  candidate, title, isBusy, isRegenerating, isCommitting,
  onRegenerateAgain, onCommit, onCancel,
}) => {
  const currentUrl = candidate.currentCoverImageUrl;
  const candidateUrl = candidate.candidateCoverUrl?.startsWith('http')
    ? candidate.candidateCoverUrl
    : `${API_URL}${candidate.candidateCoverUrl}`;
  // Media stream requires auth; append the token as a `t=` header-less
  // fallback isn't supported by the backend, so we fetch as a blob for the
  // preview <img>.
  const [candidateBlobUrl, setCandidateBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('rbr_token');
    fetch(candidateUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : null)
      .then((b) => { if (!cancelled && b) setCandidateBlobUrl(URL.createObjectURL(b)); })
      .catch(() => { /* ignore */ });
    return () => {
      cancelled = true;
      if (candidateBlobUrl) URL.revokeObjectURL(candidateBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateUrl]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="cover-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !isBusy) onCancel(); }}
    >
      <div className="bg-[#FFFCF6] rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-stone-200 flex items-baseline justify-between">
          <div>
            <h3 className="text-lg font-semibold text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Preview new emblem cover
            </h3>
            <p className="text-[12px] text-stone-500 mt-0.5 line-clamp-1">{title}</p>
          </div>
          <button onClick={onCancel} disabled={isBusy} className="text-stone-400 hover:text-stone-600 disabled:opacity-50" data-testid="cover-preview-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Current cover</p>
            <div className="aspect-square rounded-xl overflow-hidden border border-stone-200 bg-stone-100 flex items-center justify-center">
              {currentUrl
                ? <img src={currentUrl.startsWith('http') ? currentUrl : `${API_URL}${currentUrl}`} alt="Current cover" className="w-full h-full object-cover" data-testid="cover-preview-current" />
                : <span className="text-[11px] text-stone-400">No current cover</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#C79A4A] mb-2">New candidate</p>
            <div className="aspect-square rounded-xl overflow-hidden border-2 border-[#C79A4A] bg-stone-100 flex items-center justify-center">
              {isRegenerating
                ? <Loader2 className="w-8 h-8 animate-spin text-[#C79A4A]" data-testid="cover-preview-loading" />
                : candidateBlobUrl
                  ? <img src={candidateBlobUrl} alt="Candidate cover" className="w-full h-full object-cover" data-testid="cover-preview-candidate" />
                  : <Loader2 className="w-8 h-8 animate-spin text-stone-400" />}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-stone-200 flex flex-wrap gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={isBusy}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-100 disabled:opacity-60"
            data-testid="cover-preview-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onRegenerateAgain}
            disabled={isBusy}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-60"
            data-testid="cover-preview-regenerate-again"
          >
            {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Regenerate again
          </button>
          <button
            onClick={onCommit}
            disabled={isBusy || !candidateBlobUrl}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#173F35] text-[#FFFCF6] hover:bg-[#0D2B24] disabled:opacity-60"
            data-testid="cover-preview-commit"
          >
            {isCommitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Use new cover
          </button>
        </div>
      </div>
    </div>
  );
};

// -------------------- Audit log tab --------------------

const AuditLogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ actor: '', action: '', contentType: '', result: '', source: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set('page', String(page));
      q.set('pageSize', '25');
      const r = await authFetch(`/api/admin/audit-log?${q.toString()}`);
      const data = await r.json();
      if (r.ok) {
        setRows(data.items || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      }
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div data-testid="admin-audit-tab" className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <input placeholder="Actor (email)" value={filters.actor} onChange={(e) => setFilter('actor', e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="audit-filter-actor" />
        <input placeholder="Action" value={filters.action} onChange={(e) => setFilter('action', e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="audit-filter-action" />
        <select value={filters.contentType} onChange={(e) => setFilter('contentType', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="audit-filter-type">
          <option value="">Any content type</option>
          {CONTENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={filters.result} onChange={(e) => setFilter('result', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="audit-filter-result">
          <option value="">Any result</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <select value={filters.source} onChange={(e) => setFilter('source', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="audit-filter-source">
          <option value="">Any source</option>
          <option value="admin">Admin</option>
          <option value="scheduler">Scheduler</option>
          <option value="system">System</option>
        </select>
        <span className="ml-auto text-[12px] text-stone-500" data-testid="audit-total">{total} entries</span>
      </div>

      <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 overflow-hidden">
        {loading ? (
          <p className="text-center py-8 text-stone-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-stone-500">No audit rows match these filters.</p>
        ) : (
          <ul className="divide-y divide-stone-100">
            {rows.map(row => (
              <li key={row.id} className="px-4 py-3" data-testid={`audit-row-${row.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[11px] font-semibold text-[#173F35]">{row.action}</span>
                  {row.contentType && <span className="text-[10px] uppercase tracking-wider text-[#C79A4A] font-bold">{row.contentType}</span>}
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${row.result === 'failed' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>{row.result || 'success'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-stone-100 text-stone-600">{row.source || 'admin'}</span>
                  <span className="ml-auto text-[10px] text-stone-400">{new Date(row.createdAt).toLocaleString()}</span>
                </div>
                <p className="text-[11px] text-stone-500 mt-1">
                  {(row.actorEmail || row.adminEmail || 'system')} · target {row.targetType}:{(row.targetId || '').slice(0, 8)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Pager page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
};

// -------------------- Bulk import panel --------------------

const BulkImportPanel = ({ onImported }) => {
  const [text, setText] = useState('');
  const [autoPublish, setAutoPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    let items;
    try { items = JSON.parse(text); } catch { return toast.error('Invalid JSON'); }
    if (!Array.isArray(items)) return toast.error('JSON must be an array of items');
    setBusy(true);
    try {
      const r = await authFetch('/api/admin/content/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ items, autoPublish }),
      });
      const data = await r.json();
      setResult(data);
      if (r.ok) {
        toast.success(`Imported ${data.inserted}, skipped ${data.skippedCount}`);
        onImported();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch { toast.error('Network error'); } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(f);
  };

  return (
    <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-5 shadow-sm">
      <h3 className="text-xl font-semibold text-stone-800 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Bulk import</h3>
      <p className="text-sm text-stone-500 mb-4">Paste a JSON array or upload a .json file. Duplicates skipped by slug — safe to re-run.</p>
      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-1.5 cursor-pointer text-sm text-stone-700 px-3 py-2 rounded-xl bg-white border border-stone-200 hover:border-[#C79A4A]">
          <FileJson className="w-4 h-4" />
          Load .json
          <input type="file" accept="application/json,.json" className="hidden" onChange={onFile} />
        </label>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
          Auto-publish imported items
        </label>
      </div>
      <textarea rows={12} className="w-full font-mono text-[12px] px-3 py-2 rounded-xl bg-white border border-stone-200 focus:border-[#C79A4A] focus:outline-none" value={text} onChange={(e) => setText(e.target.value)} data-testid="admin-bulk-json" />
      <div className="flex justify-between items-center mt-3">
        <button onClick={submit} disabled={busy} data-testid="admin-bulk-submit" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60 active:scale-95 transition-all">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Import
        </button>
        {result && (
          <p className="text-[12px] text-stone-600">
            <span className="text-emerald-700 font-semibold">{result.inserted}</span> inserted ·{' '}
            <span className="text-stone-500">{result.skippedCount}</span> skipped
            {result.failed?.length ? <span className="text-rose-700"> · {result.failed.length} failed</span> : null}
          </p>
        )}
      </div>
    </div>
  );
};

// -------------------- Root page --------------------

const AdminContentPage = () => {
  const { user, isLoading: authLoading } = useAuth();

  const [tab, setTab] = useState('dashboard');   // dashboard | library | editor | audit | bulk
  const [dashboard, setDashboard] = useState(null);
  const [runningScheduler, setRunningScheduler] = useState(false);

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [filters, setFilters] = useState({ q: '', type: '', status: '', accessLevel: '', sort: 'createdAt_desc' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [editing, setEditing] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/dashboard');
      if (r.ok) setDashboard(await r.json());
    } catch { /* ignore */ }
  }, []);

  const loadLibrary = useCallback(async () => {
    setLoadingList(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set('page', String(page));
      q.set('pageSize', '25');
      const r = await authFetch(`/api/admin/content?${q.toString()}`);
      const data = await r.json();
      if (r.ok) {
        setRows(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } finally { setLoadingList(false); }
  }, [filters, page]);

  useEffect(() => {
    if (!user?.isAdmin) return;
    if (tab === 'dashboard') loadDashboard();
    if (tab === 'library') loadLibrary();
  }, [tab, user, loadDashboard, loadLibrary]);

  const openScheduledInEditor = async (id) => {
    const r = await authFetch(`/api/admin/content?page=1&pageSize=1&sort=createdAt_desc`); // fallback
    // Simpler: fetch admin single via list with q
    try {
      const rr = await authFetch(`/api/admin/content?q=${encodeURIComponent(id)}&pageSize=1`);
      const d = await rr.json();
      const found = (d.items || []).find(x => x.id === id) || (d.items || [])[0];
      if (found) { setEditing(found); setTab('editor'); }
      else toast.error('Item not found');
    } catch { toast.error('Network error'); }
    void r;
  };

  if (authLoading) return <div className="p-8 text-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.isAdmin) return <Navigate to="/more" replace />;

  const doLifecycle = async (action, row) => {
    if (!row?.id) return;
    if (action === 'publish') {
      const r = await authFetch(`/api/admin/content/${row.id}/publish`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) {
        const errs = data?.detail?.errors || [];
        toast.error(errs.length ? `Cannot publish: ${errs.join(' · ')}` : (typeof data.detail === 'string' ? data.detail : 'Publish failed'));
        return;
      }
      toast.success('Published'); setEditing(data); loadLibrary(); loadDashboard();
    } else if (action === 'schedule') {
      setScheduleTarget(row);
    } else if (action === 'unpublish') {
      const r = await authFetch(`/api/admin/content/${row.id}/unpublish`, { method: 'POST' });
      if (!r.ok) { const d = await r.json(); toast.error(d.detail || 'Failed'); return; }
      toast.success('Reverted to draft'); const fresh = await r.json(); setEditing(fresh); loadLibrary(); loadDashboard();
    } else if (action === 'archive') {
      if (!window.confirm('Archive this item? It stays in the DB but is hidden from users.')) return;
      const r = await authFetch(`/api/admin/content/${row.id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Archived'); setTab('library'); setEditing(null); loadLibrary(); loadDashboard(); }
      else toast.error('Failed to archive');
    } else if (action === 'restore') {
      const r = await authFetch(`/api/admin/content/${row.id}/restore`, { method: 'POST' });
      if (!r.ok) { const d = await r.json(); toast.error(d.detail || 'Failed'); return; }
      toast.success('Restored to draft'); const fresh = await r.json(); setEditing(fresh); loadLibrary(); loadDashboard();
    }
  };

  const confirmSchedule = async (isoUtc) => {
    if (!scheduleTarget) return;
    setScheduleSaving(true);
    try {
      const r = await authFetch(`/api/admin/content/${scheduleTarget.id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ publishAt: isoUtc }),
      });
      const data = await r.json();
      if (!r.ok) {
        const errs = data?.detail?.errors || [];
        toast.error(errs.length ? `Cannot schedule: ${errs.join(' · ')}` : (typeof data.detail === 'string' ? data.detail : 'Schedule failed'));
        return;
      }
      toast.success('Scheduled');
      setScheduleTarget(null);
      setEditing(data);
      loadLibrary(); loadDashboard();
    } finally { setScheduleSaving(false); }
  };

  const runScheduler = async () => {
    if (!window.confirm('Run the scheduler now? Any eligible scheduled items will be published immediately.')) return;
    setRunningScheduler(true);
    try {
      const r = await authFetch('/api/admin/scheduler/run-now', { method: 'POST' });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Scheduler failed'); return; }
      toast.success(`Processed ${data.processed} · published ${data.published} · skipped ${data.skipped} · failed ${data.failed}`);
      loadDashboard(); if (tab === 'library') loadLibrary();
    } finally { setRunningScheduler(false); }
  };

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div className="pb-32 min-h-screen bg-[#F6F1E7]" data-testid="admin-content-page">
      <div className="px-5 pt-5 pb-3">
        <Link to="/more" className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-700 mb-3">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <h1 className="text-3xl text-stone-800 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Rowdy Admin Studio</h1>
        <p className="text-sm text-stone-500">Manage every reflection, meditation, and soundscape in one place.</p>
      </div>

      <div className="px-5 mb-4 flex gap-2 flex-wrap">
        <TabButton tabKey="dashboard" current={tab} onClick={setTab} icon={LayoutDashboard}>Dashboard</TabButton>
        <TabButton tabKey="library" current={tab} onClick={setTab} icon={ListChecks}>Library</TabButton>
        <TabButton tabKey="calendar" current={tab} onClick={setTab} icon={CalendarDays}>Calendar</TabButton>
        <TabButton tabKey="media" current={tab} onClick={setTab} icon={MediaIcon}>Media Library</TabButton>
        <TabButton tabKey="programs" current={tab} onClick={setTab} icon={Sparkles}>Programs</TabButton>
        <TabButton tabKey="flagship" current={tab} onClick={setTab} icon={Sparkles}>Flagship Programs</TabButton>
        <TabButton tabKey="program-analytics" current={tab} onClick={setTab} icon={Sparkles}>Program Analytics</TabButton>
        <TabButton tabKey="launch-campaign" current={tab} onClick={setTab} icon={Sparkles}>Launch Campaign</TabButton>
        <TabButton tabKey="program-meditations" current={tab} onClick={setTab} icon={Sparkles}>Program Meditations</TabButton>
        <TabButton tabKey="editor" current={tab} onClick={(t) => { setEditing(null); setTab(t); }} icon={Plus}>New</TabButton>
        <TabButton tabKey="audit" current={tab} onClick={setTab} icon={ScrollText}>Audit Log</TabButton>
        <TabButton tabKey="bulk" current={tab} onClick={setTab} icon={Upload}>Bulk Import</TabButton>
      </div>

      <div className="px-5">
        {tab === 'dashboard' && (
          <DashboardTab
            dashboard={dashboard}
            onRefresh={loadDashboard}
            onRunScheduler={runScheduler}
            running={runningScheduler}
            onOpenScheduled={openScheduledInEditor}
          />
        )}

        {tab === 'library' && (
          <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4" data-testid="admin-library-tab">
            <LibraryFilters filters={filters} onChange={setFilter} onSearch={loadLibrary} onCreate={() => { setEditing(null); setTab('editor'); }} />
            <p className="text-[11px] text-stone-500 mb-2" data-testid="library-total">{total} items</p>
            {loadingList ? (
              <p className="text-center py-8 text-stone-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
            ) : rows.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>No content matches these filters.</p>
                <p className="text-[12px] text-stone-500 mt-1">Adjust your filters, or create a new item.</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {rows.map(row => (
                  <LibraryRow
                    key={row.id}
                    row={row}
                    onEdit={(r) => { setEditing(r); setTab('editor'); }}
                    onArchive={(r) => doLifecycle('archive', r)}
                    onPublish={(r) => doLifecycle('publish', r)}
                    onSchedule={(r) => doLifecycle('schedule', r)}
                    onUnpublish={(r) => doLifecycle('unpublish', r)}
                    onRestore={(r) => doLifecycle('restore', r)}
                  />
                ))}
              </div>
            )}
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}

        {tab === 'editor' && (
          <ContentForm
            initial={editing}
            onSaved={(fresh) => { setEditing(fresh); loadLibrary(); loadDashboard(); toast.success('Saved'); }}
            onCancel={() => { setTab('library'); setEditing(null); }}
            onLifecycle={doLifecycle}
          />
        )}

        {tab === 'audit' && <AuditLogTab />}

        {tab === 'calendar' && <CalendarTab />}

        {tab === 'media' && <MediaLibraryTab />}

        {tab === 'programs' && <ProgramsTab />}
        {tab === 'program-meditations' && <ProgramMeditationsTab />}
        {tab === 'flagship' && <FlagshipProgramsTab />}
        {tab === 'program-analytics' && <ProgramAnalyticsTab />}
        {tab === 'launch-campaign' && <LaunchCampaignTab />}

        {tab === 'bulk' && <BulkImportPanel onImported={() => { loadLibrary(); loadDashboard(); }} />}
      </div>

      <ScheduleModal
        open={!!scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onConfirm={confirmSchedule}
        row={scheduleTarget}
        saving={scheduleSaving}
      />
    </div>
  );
};

export default AdminContentPage;
